import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { CHART_MARGIN } from '../../core/constants/chart.constants';
import {
  findElementByPropertyValue,
  mapCollection,
  maxValue,
  minValue,
  push,
  reduce,
  sort
} from '../../core/utils/collection-utils';
import { Box, Typography } from '@mui/material';
import {
  calculateNiceStep,
  roundDown,
  roundUp
} from '../../core/utils/number-utils';
import { isNullOrUndefined } from '../../core/utils/object-utils';

interface ChartProps {
  title: string;
  xTitle: string;
  yTitle: string;
  series: ChartSerieType[];
  axisSteps?: { x?: number; y?: number };
  showLineLabels?: boolean;
  targetTicks?: number;
  hideLegend?: boolean;
  fixedDomain?: { x?: [number, number]; y?: [number, number] };
}

export interface ChartSerieType {
  name: string;
  color: string;
  data: { x: number; y: number }[];
}

export const Chart = (props: ChartProps) => {
  const {
    showLineLabels = false,
    targetTicks = 6,
    hideLegend = false,
    fixedDomain
  } = props;

  const points = reduce(
    (acc: any, curr: any) => {
      mapCollection((point: any) => {
        if (!findElementByPropertyValue(acc, 'x', point.x)) {
          const coordinates = { x: point.x } as any;
          mapCollection((serie, index) => {
            const found = findElementByPropertyValue(serie.data, 'x', point.x);
            coordinates['y' + index] = found ? found.y : null;
          }, props.series);
          acc = push(acc, coordinates);
        }
      }, curr.data);
      return sort(acc, (a: any, b: any) => a.x - b.x);
    },
    [],
    props.series
  ) as any[];

  const getFirstValidPoint = (
    serieIndex: number
  ): { x: number; y: number } | null => {
    for (let i = 0; i < points.length; i++) {
      const yValue = points[i][`y${serieIndex}`];
      if (!isNullOrUndefined(yValue)) {
        return { x: points[i].x, y: yValue };
      }
    }
    return null;
  };

  const getRawMin = (axis: string): number =>
    minValue(
      mapCollection(
        (serie) => minValue(mapCollection((point) => point[axis], serie.data)),
        props.series
      )
    );

  const getRawMax = (axis: string): number =>
    maxValue(
      mapCollection(
        (serie) => maxValue(mapCollection((point) => point[axis], serie.data)),
        props.series
      )
    );

  const getStep = (axis: string): number => {
    if (props.axisSteps && props.axisSteps[axis]) {
      return props.axisSteps[axis];
    }
    const range = getRawMax(axis) - getRawMin(axis);
    return calculateNiceStep(range, targetTicks);
  };

  const stepX = getStep('x');
  const stepY = getStep('y');

  const min = (axis: string) => {
    if (fixedDomain && fixedDomain[axis]) {
      return fixedDomain[axis][0];
    }
    const step = axis === 'x' ? stepX : stepY;
    return roundDown(getRawMin(axis), step);
  };

  const max = (axis: string) => {
    if (fixedDomain && fixedDomain[axis]) {
      return fixedDomain[axis][1];
    }
    const step = axis === 'x' ? stepX : stepY;
    return roundUp(getRawMax(axis), step);
  };

  const calculateChartDomain =
    (axis: string) =>
    ([dataMin, dataMax]) => {
      if (fixedDomain && fixedDomain[axis]) {
        return fixedDomain[axis] as [number, number];
      }
      const step = axis === 'x' ? stepX : stepY;
      const domainMin = roundDown(dataMin, step);
      const domainMax = roundUp(dataMax, step);
      return [domainMin, domainMax] as [number, number];
    };

  const calculateTicks = (
    start: number,
    end: number,
    step: number
  ): number[] => {
    const ticks = [];
    for (let i = start; i <= end; i = i + step) {
      ticks.push(i);
    }
    return ticks;
  };

  return (
    <Box className="custom-chart">
      <Typography variant="h6" component="h3">
        {props.title}
      </Typography>
      <ResponsiveContainer width="95%" height={400}>
        <LineChart width={400} height={400} data={points}>
          {props.series.map((serie, index) => (
            <Line
              key={serie.name}
              type="monotone"
              dataKey={`y${index}`}
              name={serie.name}
              stroke={serie.color}
              dot={false}
              strokeWidth={2}
              legendType="plainline"
              connectNulls={true}
            />
          ))}
          {showLineLabels &&
            props.series.map((serie, index) => {
              const firstPoint = getFirstValidPoint(index);
              if (!firstPoint) return null;
              return (
                <ReferenceDot
                  key={`label-${serie.name}`}
                  x={firstPoint.x}
                  y={firstPoint.y}
                  r={0}
                  label={{
                    value: serie.name,
                    position: 'left',
                    fill: serie.color,
                    fontSize: 11,
                    fontWeight: 'bold'
                  }}
                />
              );
            })}
          <XAxis
            type="number"
            label={{
              value: props.xTitle,
              position: 'insideBottom',
              offset: -5
            }}
            dataKey="x"
            domain={calculateChartDomain('x')}
            padding={{ left: CHART_MARGIN, right: CHART_MARGIN }}
            ticks={calculateTicks(min('x'), max('x'), stepX)}
            tickFormatter={(value) =>
              parseFloat(Number(value).toFixed(2)).toString()
            }
          />
          <YAxis
            type="number"
            label={{
              value: props.yTitle,
              angle: -90,
              position: 'insideLeft',
              offset: 5
            }}
            domain={calculateChartDomain('y')}
            padding={{ top: CHART_MARGIN, bottom: CHART_MARGIN }}
            ticks={calculateTicks(min('y'), max('y'), stepY)}
            tickFormatter={(value) =>
              parseFloat(Number(value).toFixed(2)).toString()
            }
          />
          <Tooltip />
          {!hideLegend && (
            <Legend verticalAlign="top" align="right" height={35} />
          )}
          <CartesianGrid strokeDasharray="3 3" />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
